import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Zap, Eye, EyeOff, Mail, ShieldX } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState("owner");
  const [ipCheck, setIpCheck] = useState({ loading: true, allowed: true, ip: "" });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/check-ip`)
      .then(r => setIpCheck({ loading: false, allowed: r.data.allowed, ip: r.data.ip }))
      .catch(() => setIpCheck({ loading: false, allowed: true, ip: "unknown" }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(password, mode === "staff" ? email : "");
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.includes("not whitelisted")) {
        toast.error(detail);
      } else {
        toast.error(mode === "staff" ? "Invalid email or password." : "Incorrect password. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (ipCheck.loading) {
    return (
      <div className="min-h-screen bg-[#FFD600] flex items-center justify-center" data-testid="admin-ip-loading">
        <div className="animate-pulse font-bold text-lg">Verifying access...</div>
      </div>
    );
  }

  if (!ipCheck.allowed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="admin-ip-blocked">
        <div className="text-center max-w-md">
          <ShieldX size={64} className="text-red-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Access Denied</h1>
          <p className="text-gray-400 mb-6">Your IP address <span className="text-white font-mono font-bold">{ipCheck.ip}</span> is not authorised to access the admin panel.</p>
          <p className="text-gray-500 text-sm">Contact the site owner to whitelist your IP.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFD600] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-12 h-12 bg-black border-2 border-black rounded-xl flex items-center justify-center">
              <Zap size={24} className="text-[#FFD600]" fill="#FFD600" />
            </div>
            <div className="text-left">
              <p className="font-black text-xl leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>No Fuss Websites</p>
              <p className="text-xs font-bold uppercase tracking-widest">Admin Panel</p>
            </div>
          </div>
          <h1 className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Welcome Back</h1>
          <p className="font-semibold text-sm mt-1 opacity-70">
            {mode === "owner" ? "Enter your admin password" : "Sign in with your staff account"}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border-2 border-black rounded-2xl p-8"
          style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}
          data-testid="admin-login-form"
        >
          <div className="flex border-2 border-black rounded-xl overflow-hidden mb-6">
            <button type="button" onClick={() => setMode("owner")}
              className={`flex-1 py-2.5 text-sm font-bold transition-colors ${mode === "owner" ? "bg-black text-white" : "bg-white hover:bg-gray-50"}`}
              data-testid="login-mode-owner">
              Owner
            </button>
            <button type="button" onClick={() => setMode("staff")}
              className={`flex-1 py-2.5 text-sm font-bold transition-colors border-l-2 border-black ${mode === "staff" ? "bg-black text-white" : "bg-white hover:bg-gray-50"}`}
              data-testid="login-mode-staff">
              Staff
            </button>
          </div>

          {mode === "staff" && (
            <div className="mb-4">
              <label className="text-sm font-black uppercase tracking-wider mb-2 block">Email</label>
              <div className="relative">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@nofusswebsites.com.au"
                  className="w-full border-2 border-black rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="admin-email-input" />
                <Mail size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          )}

          <div className="mb-6">
            <label className="text-sm font-black uppercase tracking-wider mb-2 block">Password</label>
            <div className="relative">
              <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "owner" ? "Enter admin password" : "Enter your password"}
                className="w-full border-2 border-black rounded-xl px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-black"
                data-testid="admin-password-input" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading || !password || (mode === "staff" && !email)}
            className="w-full bg-black text-white font-black py-4 rounded-xl text-lg border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ boxShadow: "4px 4px 0px 0px #FFD600" }}
            data-testid="admin-login-submit">
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}
